import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAppHostname } from "@/helpers/apiHelpers";
import { useProviderCode } from "@/hooks";
import { useLazyVerifyProviderQuery } from "@/redux/apis/auth/authApi";
import { useLazyGetSiteInfoQuery } from "@/redux/apis/resource/resourceApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setConfig, setKindeConfig, setSiteInfo } from "@/redux/slices/config/configSlice";
import { configSelector } from "@/redux/slices/config/selectors";
import { SiteConfig, SiteInfo } from "@/redux/slices/config/types";
import { Operator } from "@/redux/slices/operator/types";
import { transformLegacySiteConfig } from "../helpers";

export type LegacySiteConfig = {
  siteInfo: SiteInfo;
  operator: Operator;
};

type UseLegacySiteConfig =
  | {
      isReady: false;
      config: undefined;
    }
  | {
      isReady: true;
      config: SiteConfig;
    };

const hostname = getAppHostname();

export const useLegacySiteConfig = (): UseLegacySiteConfig => {
  const dispatch = useAppDispatch();
  const config = useAppSelector(configSelector);

  const [getSiteInfo] = useLazyGetSiteInfoQuery();
  const [verify] = useLazyVerifyProviderQuery();

  const [params] = useSearchParams();
  const type = params.get("type");
  const providerCode = useProviderCode();

  const navigate = useNavigate();

  useEffect(() => {
    getSiteInfo(hostname)
      .unwrap()
      .then((siteInfo) => {
        // Set siteInfo immediately so it's available even if verify fails
        dispatch(setSiteInfo(siteInfo));

        verify({ providerCode: providerCode || siteInfo.key, apiUrl: siteInfo.apiUrl, forDriver: type === "driver" })
          .unwrap()
          .then((operator) => {
            if (operator.kindeIssuerUrl && operator.kindeClientId) {
              dispatch(setKindeConfig({ domain: operator.kindeIssuerUrl, clientId: operator.kindeClientId }));
            }
            dispatch(setConfig(transformLegacySiteConfig({ siteInfo, operator })));
          })
          .catch((e) => {
            console.error("Unable to verify provider", {
              hostname,
              providerCode: providerCode || siteInfo.key,
              error: e,
            });
            navigate("/404");
          });
      })
      .catch((e) => {
        console.error("Unable to configure provider", {
          hostname,
          error: e,
        });
        navigate("/404");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!config) return { isReady: false, config: undefined };
  return { isReady: true, config };
};
